import { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';

import { fetchEmployee, fetchEmployeeEmployment } from '~/data/employee';
import { fetchEmployeeDetails } from '~/data/employeeProfile';
import { useAuthentication } from '~/data/user/useAuthentication';
import {
  DispatchReduxFetch,
  useInitializeCommonReduxData,
} from '~/hooks/initializeData';
import { LibraryComponentProviders } from '~/library/components/LibraryComponentProviders';
import { Providers } from '~/Providers';
import { useEnabledModuleTabPaths } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useEnabledModuleTabPaths';
import {
  EmployeeModuleRouting_Rewarding,
  EmployeeModuleTabPath_Rewarding,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/RewardingModule/data';
import { EmployeeProposalsDialog } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog';
import { EmployeeProposalsDialogProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/component';
import { convertRewardingModuleTabsToEmployeeProposalDialogTabs } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/data';
import { AppDispatch } from '~/store';

export type WorkflowsEmployeeProposalsDialog_OnUpdateLegacy = {
  onUpdateLegacy?: (updateReferences: string[]) => void;
};

export type WorkflowsEmployeeProposalsDialogProps =
  WorkflowsEmployeeProposalsDialog_OnUpdateLegacy & {
    employeeId: number;
  } & Omit<EmployeeProposalsDialogProps, 'enabledDialogTabs'>;

export const WorkflowsEmployeeProposalsDialog = (
  props: WorkflowsEmployeeProposalsDialogProps,
) => {
  const { employeeId } = props;
  const { userDetails, isAuthenticated } = useAuthentication();

  const {
    requiredStatus: { isInitializing },
  } = useInitializeCommonReduxData({
    required: [
      DispatchReduxFetch.ApplicationConfig,
      DispatchReduxFetch.AllEnums,
      DispatchReduxFetch.Languages,
      DispatchReduxFetch.AllEmployees,
      DispatchReduxFetch.AllOrgUnits,
    ],
  });

  const dispatch: AppDispatch = useDispatch();
  useEffect(() => {
    if (isAuthenticated && !!userDetails) {
      dispatch(fetchEmployee({ empId: `${employeeId}` }));
      dispatch(fetchEmployeeEmployment({ empId: `${employeeId}` }));
      dispatch(fetchEmployeeDetails(`${employeeId}`));
    }
  }, [dispatch, isAuthenticated, userDetails, employeeId]);

  const { enabledTabPaths } = useEnabledModuleTabPaths({
    ...EmployeeModuleRouting_Rewarding,
    availableTabs: [
      EmployeeModuleTabPath_Rewarding.General,
      EmployeeModuleTabPath_Rewarding.Incentives,
      EmployeeModuleTabPath_Rewarding.BillableHours,
      EmployeeModuleTabPath_Rewarding.Benefits,
    ],
  });

  const enabledDialogTabs = useMemo(
    () =>
      convertRewardingModuleTabsToEmployeeProposalDialogTabs(enabledTabPaths),
    [enabledTabPaths],
  );

  if (!isAuthenticated || isInitializing) {
    return null;
  }

  return (
    <LibraryComponentProviders>
      <EmployeeProposalsDialog
        {...props}
        enabledDialogTabs={enabledDialogTabs}
      />
    </LibraryComponentProviders>
  );
};

export const renderWorkflowsEmployeeProposalsDialog = (
  containerId: string,
  props: WorkflowsEmployeeProposalsDialogProps,
) => {
  ReactDOM.render(
    <Providers>
      <WorkflowsEmployeeProposalsDialog {...props} />
    </Providers>,
    document.getElementById(containerId),
  );
};
